import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import styles from './AdminLayout.module.css';

export default function AdminBlogs() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    status: 'draft',
  });
  const [error, setError] = useState('');
  const [seoBusy, setSeoBusy] = useState(false);
  const [seoError, setSeoError] = useState('');
  const [seoInfo, setSeoInfo] = useState(null);

  // Cloudflare worker base URL for AI summarize
  const WORKER_URL = 'https://sync.rocketcitynerd.com';

  const fetchPosts = async () => {
    setLoading(true);
    setError('');
    let query = supabase
      .from('posts')
      .select('*')
      .order('published_at', { ascending: false });
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }
    const { data, error } = await query;
    if (error) setError(error.message);
    else setPosts(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts();
  }, [statusFilter]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const generateSeoExcerpt = async () => {
    setSeoError('');
    setSeoInfo(null);
    if (!form.content || !form.title) {
      setSeoError('Add a title and content first.');
      return;
    }
    try {
      setSeoBusy(true);
      const text = `Title: ${form.title}\n\nContent: ${form.content}`.slice(0, 8000);
      const res = await fetch(`${WORKER_URL}/api/ai/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          system: 'Write a single SEO meta description for this post in under 155 characters. Be factual, clear, and inviting. Third-person, no emojis, no hashtags. Output plain text only.',
          maxTokens: 80,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
      let summary = (data?.summary || '').toString().trim();
      // Enforce ~155 char limit
      const limit = 155;
      if (summary.length > limit) {
        summary = summary.slice(0, limit - 1);
        // Trim incomplete last word
        const lastSpace = summary.lastIndexOf(' ');
        if (lastSpace > 80) summary = summary.slice(0, lastSpace);
      }
      setForm((f) => ({ ...f, excerpt: summary }));
      setSeoInfo({ provider: data?.provider, model: data?.model });
    } catch (err) {
      setSeoError(err instanceof Error ? err.message : String(err));
    } finally {
      setSeoBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title || !form.slug) {
      setError('Title and slug are required.');
      return;
    }
    setLoading(true);
    if (editingPost) {
      const { error } = await supabase
        .from('posts')
        .update({
          title: form.title,
          slug: form.slug,
          excerpt: form.excerpt,
          content: form.content,
          category: form.category,
          status: form.status,
        })
        .eq('id', editingPost.id);
      if (error) setError(error.message);
      else setEditingPost(null);
    } else {
      const { error } = await supabase.from('posts').insert([
        {
          title: form.title,
          slug: form.slug,
          excerpt: form.excerpt,
          content: form.content,
          category: form.category,
          status: form.status,
        },
      ]);
      if (error) setError(error.message);
    }
    setForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      status: 'draft',
    });
    await fetchPosts();
    setLoading(false);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category || '',
      status: post.status || 'draft',
    });
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError('');
    const { error } = await supabase.from('posts').delete().eq('id', id);
    if (error) setError(error.message);
    await fetchPosts();
    setLoading(false);
  };

  const handleCancel = () => {
    setEditingPost(null);
    setForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      status: 'draft',
    });
  };

  return (
    <div className="admin-blogs-container" style={{ maxWidth: 700, margin: 'auto', padding: 24 }}>
      <h2>Manage Blog Posts</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
        <h3>{editingPost ? 'Edit Post' : 'Add New Post'}</h3>
        <div>
          <label>
            Title:
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              style={{ width: '100%', marginBottom: 8 }}
            />
          </label>
        </div>
        <div>
          <label>
            Slug:
            <input
              name="slug"
              value={form.slug}
              onChange={handleChange}
              required
              style={{ width: '100%', marginBottom: 8 }}
            />
          </label>
        </div>
        <div>
          <label>
            Excerpt:
            <textarea
              name="excerpt"
              value={form.excerpt}
              onChange={handleChange}
              rows={2}
              style={{ width: '100%', marginBottom: 8 }}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px' }}>
            <button
              type="button"
              onClick={generateSeoExcerpt}
              disabled={seoBusy || !form.content || !form.title}
              className={styles?.button}
              style={{ padding: '6px 10px' }}
              title={!form.content ? 'Enter content first' : 'Use AI to generate a short SEO summary'}
            >
              {seoBusy ? 'Generating…' : 'Generate SEO summary'}
            </button>
            {seoError && <span style={{ color: 'red', fontSize: 12 }}>{seoError}</span>}
            {!seoError && seoInfo && (
              <span className={styles?.muted} style={{ fontSize: 12 }}>
                AI: {seoInfo.provider || 'unknown'}{seoInfo.model ? `/${seoInfo.model}` : ''}
              </span>
            )}
          </div>
        </div>
        <div>
          <label>
            Content:
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={6}
              style={{ width: '100%', marginBottom: 8 }}
            />
          </label>
        </div>
        <div>
          <label>
            Category:
            <input
              name="category"
              value={form.category}
              onChange={handleChange}
              style={{ width: '100%', marginBottom: 8 }}
            />
          </label>
        </div>
        <div>
          <label>
            Status:
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              style={{ width: '100%', marginBottom: 8 }}
            >
              <option value="draft">draft</option>
              <option value="published">published</option>
              <option value="archived">archived</option>
            </select>
          </label>
        </div>
        {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ marginRight: 8 }}>
          {editingPost ? 'Update Post' : 'Add Post'}
        </button>
        {editingPost && (
          <button type="button" onClick={handleCancel}>
            Cancel
          </button>
        )}
      </form>
      <hr style={{ margin: '24px 0' }} />
      <h3>Post List</h3>
      <div style={{ marginBottom: 12 }}>
        <label>
          Status filter:
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ marginLeft: 8 }}
          >
            <option value="all">All</option>
            <option value="draft">draft</option>
            <option value="published">published</option>
            <option value="archived">archived</option>
          </select>
        </label>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : posts.length === 0 ? (
        <p>No posts found.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Published</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id}>
                <td>{post.title}</td>
                <td>{post.slug}</td>
                <td>{post.status}</td>
                <td>
                  {post.published_at
                    ? new Date(post.published_at).toLocaleDateString()
                    : ''}
                </td>
                <td>
                  <button onClick={() => handleEdit(post)} style={{ marginRight: 8 }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(post.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
